import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import dayjs from '../_utils/dayjs.config';
import { Address, AddressBase } from '../_interfaces/address';
import { Appointment } from '../_interfaces/appointment';
import { ContactPerson } from '../_interfaces/ContactPerson';
import { EditArt } from '../_interfaces/EditArt';
import { Employee, EmployeeGroupe } from '../_interfaces/Employee';
import { HttpFunctionResult } from '../_interfaces/HttpFunctionResult';
import { TicketBase } from '../_interfaces/ticket';
import { ITicketState } from '../_interfaces/TicketState';
import { TicketZeit } from '../_interfaces/TicketTime';
import { MailSettings } from '../_pages/new-settings/mail-settings/mail-settings.component';
import { CustomerLogin } from '../_pages/new-settings/ticket-settings/customer-login-edit/customer-login-edit.page';
import { TaskTemplateBase, TaskTemplateItem } from '../_pages/new-settings/ticket-settings/task-template-overview/task-template-overview.page';
import { WorkstepContent } from '../_template/ticket-detail/workstep-content/workstep-content.component';
import { AddTimePage } from '../_template/ticket-more/ticket-detail-zeiten/add-time/add-time.page';
import { FeedbackService } from './feedback.service';
import { HttpService } from './http.service';
import { TicketEmployee } from './tickets/ticket.service';
import { TicketTag } from './boards/board.service';
import { LocalNotificationService } from './notifications/local-notification.service';
import { SocketsService } from './sockets.service';

export namespace SaveService{
  @Injectable({
    providedIn: 'root'
  })
  export class Ticket {

    public generalLoadings: {art: boolean, calculate: boolean, contact: boolean, prio: boolean, address: boolean, contactPerson: boolean} = {
      art: false,
      calculate: false,
      contact: false,
      prio: false,
      address: false,
      contactPerson: false
    }

    constructor(
      private http: HttpService,
      private ticketSrv: TicketEmployee.TicketService,
      private feedback: FeedbackService,
      private socketSrv: SocketsService,
      private localNotificationSrv: LocalNotificationService
    ) { }

    /**
     * Speichert eine bearbeitete Ticket-Art.
     * @param art Die bearbeitete Ticket-Art.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit dem Ergebnis der Anfrage enthält.
     */
    public async saveTicketArt(art: EditArt): Promise<HttpFunctionResult<any>> {
      this.generalLoadings.art = true
      const result = await this.saveAnEditArt("ticketart", {
        artId: art.id,
        workstepId: this.ticketSrv.activeWorkstepId,
        ticketId: this.ticketSrv.ticket.id
      })
      this.generalLoadings.art = false
      return result
    }

    /**
     * Speichert eine bearbeitete Kunst in der "calculate" Phase.
     * @param toCalc - Die bearbeitete Kunst, die gespeichert werden soll.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt enthält.
     */
    public async saveToCalculate(toCalc: EditArt): Promise<HttpFunctionResult<any>> {
      this.generalLoadings.calculate = true
      const result = await this.saveAnEditArt("calculate", {
        toCalculateState: toCalc.id,
        workstepId: this.ticketSrv.activeWorkstepId,
        ticketId: this.ticketSrv.ticket.id
      })
      this.generalLoadings.calculate = false
      return result
    }

    /**
     * Speichert eine bearbeitete Kontaktart.
     * @param contactArt - Die bearbeitete Kontaktart.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit dem Ergebnis der Anfrage enthält.
     */
    public async saveContactArt(contactArt: EditArt): Promise<HttpFunctionResult<any>> {
      this.generalLoadings.contact = true
      const result = await this.saveAnEditArt("contactart", {
        contactArt: contactArt.id,
        workstepId: this.ticketSrv.activeWorkstepId,
        ticketId: this.ticketSrv.ticket.id
      })
      this.generalLoadings.contact = false
      return result
    }

    /**
     * Speichert die Priorität eines Tickets.
     * @param prio - Die bearbeitete Priorität.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit beliebigen Daten zurückgibt.
     */
    public async savePriority(prio: EditArt): Promise<HttpFunctionResult<any>> {
      this.generalLoadings.prio = true
      const result = await this.saveAnEditArt("priority", {
        priority: prio.id,
        ticketId: this.ticketSrv.ticket.id
      })
      this.generalLoadings.prio = false
      return result
    }
    
    /**
     * Speichert den übergebenen Prozess für das aktuelle Ticket und den aktiven Workstep.
     * @param process Die Prozessnummer, die gespeichert werden soll.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit beliebigen Daten zurückgibt.
     */
    public async saveProcess(process: number): Promise<HttpFunctionResult<any>> {
      return await this.saveAnEditArt("process", {
        process: process,
        workstepId: this.ticketSrv.activeWorkstepId,
        ticketId: this.ticketSrv.ticket.id
      })
    }

    /**
     * Speichert eine bearbeitete Kunst.
     * @param path - Der Pfad, unter dem die Kunst gespeichert werden soll.
     * @param obj - Das Objekt, das die bearbeitete Kunst enthält.
     * @param func - Der Name der Funktion, die aufgerufen wird.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt enthält.
     */
    private async saveAnEditArt(path: string,  obj: any): Promise<HttpFunctionResult<any>>{
      return await this.http.PUT<any, any>({
        path: `ticket/save/${path}`,
        body: obj,
      })
    }

    /**
     * Fügt einen Editor hinzu oder entfernt ihn aus einem Ticket oder Workstep.
     * @param editorId - Die ID des Editors.
     * @param toAdd - Ein boolescher Wert, der angibt, ob der Editor hinzugefügt oder entfernt werden soll.
     * @param ticketId - Die ID des Tickets, zu dem der Editor hinzugefügt oder entfernt werden soll.
     * @param workstepId - Die ID des Worksteps, zu dem der Editor hinzugefügt oder entfernt werden soll.
     * @param isObserver - Ein boolescher Wert, der angibt, ob der Editor ein Beobachter ist.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt enthält.
     */
    public async saveOrDeleteEditor(editorId: number, toAdd: boolean, ticketId?: number, workstepId?: number, isObserver: boolean = false): Promise<HttpFunctionResult<any>> {
      return await this.saveOrDeleteEditorOrGroupe({
        id: editorId,
        isAdd: toAdd,
        isEmployee: true,
        ticketId: ticketId,
        workstepId: workstepId,
        isObserver: isObserver
      })
    }
    
    /**
     * Speichert oder löscht eine Gruppe.
     * @param groupeId - Die ID der Gruppe.
     * @param toAdd - Ein boolescher Wert, der angibt, ob die Gruppe hinzugefügt oder gelöscht werden soll.
     * @param ticketId - Die ID des Tickets, falls vorhanden.
     * @param workstepId - Die ID des Arbeitsschritts, falls vorhanden.
     * @param isObserver - Ein boolescher Wert, der angibt, ob die Gruppe ein Beobachter ist.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt enthält.
     */
    public async saveOrDeleteGroupe(groupeId: number, toAdd: boolean, ticketId?: number, workstepId?: number, isObserver: boolean = false): Promise<HttpFunctionResult<any>> {
      return await this.saveOrDeleteEditorOrGroupe({
        id: groupeId,
        isAdd: toAdd,
        isEmployee: false,
        ticketId: ticketId,
        workstepId: workstepId,
        isObserver: isObserver
      })
    }

    /**
     * Speichert oder löscht einen Editor oder eine Gruppe.
     * 
     * @param object - Das Objekt, das gespeichert oder gelöscht werden soll.
     * @returns Ein Promise, das ein HttpFunctionResult mit dem Ergebnis der Operation enthält.
     */
    private async saveOrDeleteEditorOrGroupe(object: any): Promise<HttpFunctionResult<any>>{
      object["workstepId"] = object["workstepId"] ? object["workstepId"] : this.ticketSrv.activeWorkstepId
      object["ticketId"] = object["ticketId"] ? object["ticketId"] : this.ticketSrv.ticket.id
      return await this.http.PUT<any, any>({
        path: "ticket/save/employee",
        body: object,
      })
    }

    /**
     * Speichert Zeit auf Server und berichtigt Zeit die über 24 Stunden laufen
     * @param {TicketZeit} time Zeit die gespeichert werden soll 
     * @param {TicketBase = null } ticket Ticket mit TicketID -> diese wird nur benötigt 
     * @param {number = null} workstepId  Id des Arbeitsschrittes
     * @param {number = null} taskId ID einer Tätigkeit 
     * @returns 
     */
    public async saveTicketTime(time: TicketZeit, ticket?: TicketBase, workstepId?: number, taskId?: number): Promise<HttpFunctionResult<any>> {
      return new Promise(async resolve=>{
        time.ticketId = ticket ? ticket.id : this.ticketSrv.ticket.id
        time.workstepId = workstepId ? workstepId : this.ticketSrv.activeWorkstepId
        time.taskId = taskId || 0
        time.timeFromAsISOString = dayjs(time.timeFrom).format()
        time.timeToAsISOString = dayjs(time.timeTo).format()

        this.http.POST<TicketZeit, TicketZeit>({
          path: "ticket/time",
          body: time,
          statusResult: async (status: number, result: HttpFunctionResult<TicketZeit>)=>{
            if(result.success && status !== 409) {
              if(time.isNewTime) await this.localNotificationSrv.sendWorkingTimeNotification(ticket?.description, result.data.id, result.data.timeFrom)
              else await this.localNotificationSrv.stopWorkingTimeNotification(time.id)

              return resolve(result) 
            }

            if(time.id !== 0 && status === 409){
              if(!result?.data) return resolve(result)
  
              //Zurückgebene Zeit von Server beinhaltet Ticket ID und Workstep ID deshalb mit der Arbeiten
              let timeFromserver: TicketZeit = result.data
              await this.feedback.openModal<TicketZeit>(AddTimePage, {ticketTime: timeFromserver, type: "correct24Time"}, async (result: HttpFunctionResult<TicketZeit>)=>{
                if(!result.success) {
                  resolve({success: false})
                  return
                }
  
                //Damit neue Zeit am Server erstellt wird weil alte gelöscht wurde
                result.data.id = 0
  
                //Neue Zeit anlegen an Server mit selben Methode
                await this.feedback.updateLoading("Speichern...")
                const newTimeResult: HttpFunctionResult<any> = await this.saveTicketTime(result.data, {id: timeFromserver.ticketId}, timeFromserver.workstepId, timeFromserver.taskId)
                await this.feedback.hideLoading()
                resolve(newTimeResult)
                return
              }, false)
              
            }
          }
        })
      })
    }

    /**
     * Speichert den Inhalt eines Arbeitsschritts.
     * @param content Der zu speichernde Inhalt.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit beliebigem Inhalt zurückgibt.
     */
    public async saveWorkstepContent(content: WorkstepContent): Promise<HttpFunctionResult<any>> {
      return await this.http.PUT<WorkstepContent, any>({
        path: `ticket/worksteps/content/${content.id}`,
        body: content,
      })
    }


    /**
     * Speichert eine Adresse für das aktuelle Ticket.
     * @param address - Die Adresse, die gespeichert werden soll.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit einem numerischen Wert enthält.
     */
    public async saveAddress(address: AddressBase): Promise<HttpFunctionResult<number>> {
      this.generalLoadings.address = true

      const result: HttpFunctionResult<number> = await this.http.PUT<any, number>({
        path: "ticket/save/address",
        body: {
          addressId: address.managerId,
          ticketId: this.ticketSrv.ticket.id,
          workstepId: this.ticketSrv.activeWorkstepId
        },
      })
      this.generalLoadings.address = false
      if(result.success) this.ticketSrv.ticket.address.id = result.data
      return result
    }

    /**
     * Speichert die Kontaktperson für den aktuellen Arbeitsschritt des Tickets.
     * @param contactPersonManagerId Die ID des Managers der Kontaktperson.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit beliebigen Daten zurückgibt.
     */
    public async saveContactPerson(contactPersonManagerId: number): Promise<HttpFunctionResult<any>> {
      this.generalLoadings.contactPerson = true
      const result: HttpFunctionResult<number> = await this.http.PUT<any, number>({
        path: `ticket/save/worksteps/${this.ticketSrv.activeWorkstepId}/contactperson`,
        body: {
          addressId: this.ticketSrv.ticket.address.id,
          managerId: contactPersonManagerId
        },
      })
      this.generalLoadings.contactPerson = false
      if(result.success) this.ticketSrv.contactPerson.id = result.data
      return result
    }

    /**
     * Speichert einen Termin für den aktuellen Arbeitsablauf oder einen bestimmten Arbeitsablauf.
     * @param workstepId - Die ID des Arbeitsablaufs, für den der Termin gespeichert werden soll. Wenn nicht angegeben, wird der aktive Arbeitsablauf verwendet.
     * @param appointment - Der Termin, der gespeichert werden soll. Wenn nicht angegeben, wird der aktuelle Termin des Ticket-Service verwendet.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt zurückgibt.
     */
    public async saveAppointment(workstepId?: number, appointment?: Appointment): Promise<HttpFunctionResult<any>>{
      return await this.http.PUT<Appointment, any>({
        path: `ticket/save/worksteps/${workstepId ? workstepId : this.ticketSrv.activeWorkstepId}/appointment`,
        body: appointment ? appointment : this.ticketSrv.appointment
      })
    }

    /**
     * Speichert den Zustand des Tickets.
     * @param state Der Zustand des Tickets, der gespeichert werden soll.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit beliebigen Daten enthält.
     */
    public async saveTicketState(state: ITicketState): Promise<HttpFunctionResult<any>>{
      this.ticketSrv?.ticketDetailChanged({
        type: "STATE_CHANGE",
        ticketId: this.ticketSrv.ticket.id,
        data: state
      })
      this.socketSrv.invoke("BoardItemChanged", this.ticketSrv.ticket.id, state.id, 0)
      return await this.http.PUT<any, any>({
        path: "ticket/save/ticketState",
        body: {
          ticketId: this.ticketSrv.ticket.id,
          stateId: state.id
        }
      })
    }

    /**
     * Speichert den Namen des aktuellen Arbeitsschritts.
     * @param name Der Name des Arbeitsschritts.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit beliebigem Inhalt zurückgibt.
     */
    public async saveWorkstepName(name: string): Promise<HttpFunctionResult<any>>{
      return await this.http.PUT<{name: string}, any>({
        path: `ticket/save/worksteps/description/${this.ticketSrv.activeWorkstepId}`,
        body: {name: name}
      })
    }

    /**
     * Aktualisiert den Namen des aktuellen Tickets.
     * @param name Der neue Name des Tickets.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit beliebigem Inhalt zurückgibt.
     */
    public async saveTicketName(name: string): Promise<HttpFunctionResult<any>>{
      this.ticketSrv.ticketDetailChanged({
        type: "NAME_CHANGED",
        ticketId: this.ticketSrv.ticket.id,
        data: name
      })
      return await this.http.PUT<{name: string}, any>({
        path: `ticket/save/name/${this.ticketSrv.ticket.id}`,
        body: {name: name}
      })
    }

    /**
     * Speichert den Online-Release-Status des aktuellen Tickets auf dem Server und sendet optional eine E-Mail an die Kontaktperson.
     * @param sendMailToContactPerson - Gibt an, ob eine E-Mail an die Kontaktperson gesendet werden soll.
     * @param contactPersonId - Die ID der Kontaktperson, an die die E-Mail gesendet werden soll.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt zurückgibt.
     */
    public async saveOnlineRelease(sendMailToContactPerson: boolean = false, contactPersonId: number = 0): Promise<HttpFunctionResult<any>>{
      return new Promise(async resolve=>{
        await this.http.PUT<any, any>({
          path: `ticket/save/onlineRelease`,
          body: {
            ticketId: this.ticketSrv.ticket.id, 
            state: this.ticketSrv.ticket.onlineRelease, 
            sendMailToContactPerson: sendMailToContactPerson, 
            contactPersonId: contactPersonId, 
            serverURL: this.http.getbaseUrl.replace("/api/", "")
          },
          statusResult: (status: number, result: HttpFunctionResult<any>)=>{
            if(result.success || status == 400) return resolve({success: true})
            return resolve(result)
          }
        })
      })
    }
    
    /**
     * Speichert die Zielzeit für den aktuellen Arbeitsschritt des Tickets.
     * @param targetTime Die neue Zielzeit in Millisekunden.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit beliebigem Inhalt zurückgibt.
     */
    public async saveTargettime(targetTime: number): Promise<HttpFunctionResult<any>>{
      return await this.http.PUT<number, any>({
        path: `ticket/save/worksteps/${this.ticketSrv.activeWorkstepId}/targettime`,
        body: targetTime
      })
    }

    /**
     * Fügt einem Ticket einen Tag hinzu.
     * @param tagId Die ID des Tags, der hinzugefügt werden soll.
     * @returns Ein Promise, das null zurückgibt, wenn die Anfrage erfolgreich war.
     */
    public async addTagToTicket(tagId: number){
      return await this.http.POST<any, null>({
        path: `ticket/save/worksteps/${this.ticketSrv.activeWorkstepId}/tag?tagid=${tagId}`,
        body: {},
      })
    }

    /**
     * Entfernt ein Tag von einem Ticket-Workstep.
     * @param tagId Die ID des Tags, der entfernt werden soll.
     * @returns Ein Promise, das ein beliebiges Ergebnis zurückgibt.
     */
    public async removeTagFromTicket(tagId: number){
      return await this.http.DELETE<any>({path: `ticket/save/worksteps/${this.ticketSrv.activeWorkstepId}/tag?tagid=${tagId}`})
    }
  }

  @Injectable({
    providedIn: 'root'
  })
  export class Settings{
    constructor(
      private http: HttpService
    ){}

    /**
     * Erstellt einen neuen Ticketstatus.
     * @param state - Der zu erstellende Ticketstatus.
     * @returns Ein Promise, das den erstellten Ticketstatus enthält.
     */
    public async createTicketState(state: ITicketState): Promise<HttpFunctionResult<ITicketState>>{
      return await this.http.POST<ITicketState, ITicketState>({
        path: "settings/ticketstates",
        body: state
      })
    }
    
    /**
     * Aktualisiert den Status eines Tickets.
     * @param state Der neue Status des Tickets.
     * @returns Ein Promise, das den aktualisierten Status des Tickets enthält.
     */
    public async updateTicketState(state: ITicketState): Promise<HttpFunctionResult<ITicketState>>{
      return await this.http.PUT<ITicketState, ITicketState>({
        path: "settings/ticketstates",
        body: state
      })
    }

    /**
     * Löscht den Ticketstatus mit der angegebenen ID.
     * @param id - Die ID des zu löschenden Ticketstatus.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit einem boolean success-Feld zurückgibt, das angibt, ob das Löschen erfolgreich war.
     */
    public async deleteTicketState(id: number): Promise<HttpFunctionResult<any>>{
      return await this.http.DELETE<any>({path: `settings/ticketstates/${id}`})
    }

    /**
     * Erstellt ein neues Ticket-Art-Objekt.
     * @param art Das zu erstellende Ticket-Art-Objekt.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit dem erstellten Ticket-Art-Objekt enthält.
     */
    public async createTicketArt(art: EditArt): Promise<HttpFunctionResult<EditArt>>{
      return await this.http.POST<EditArt, EditArt>({
        path: "settings/art",
        body: art
      })
    }

    /**
     * Aktualisiert die Art eines Tickets.
     * @param art - Die zu aktualisierende Ticketart.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit der aktualisierten Ticketart enthält.
     */
    public async updateTicketArt(art: EditArt): Promise<HttpFunctionResult<EditArt>>{
      return await this.http.PUT<EditArt, EditArt>({
        path: "settings/art",
        body: art 
      })
    }

    /**
     * Löscht ein Ticket-Art-Objekt anhand der übergebenen ID.
     * @param id - Die ID des zu löschenden Ticket-Art-Objekts.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit einem boolean success-Feld zurückgibt, das angibt, ob das Löschen erfolgreich war, oder ein HttpFunctionResult-Objekt mit einem Fehler-Feld, das den aufgetretenen Fehler enthält.
     */
    public async deleteTicketArt(id: number): Promise<HttpFunctionResult<any>>{
      return await this.http.DELETE<any>({path: `settings/art/${id}`})
    }

    /**
     * Speichert eine Ticket-Nummer Vorlage.
     * @param template Die Vorlage, die gespeichert werden soll.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit einem boolean success-Feld zurückgibt.
     */
    public async saveTicketNumberTemplate(template: string): Promise<HttpFunctionResult<any>>{
      return await this.http.PUT<{template: string}, any>({
        path: "settings/ticketnumbers",
        body: {template: template}
      })
    }

    /**
     * Erstellt einen neuen Mitarbeiter.
     * @param employee Der Mitarbeiter, der erstellt werden soll.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit dem erstellten Mitarbeiter enthält, falls die Anfrage erfolgreich war, andernfalls ein HttpFunctionResult-Objekt mit einer Fehlermeldung.
     */
    public async createEmployee(employee: Employee): Promise<HttpFunctionResult<Employee>>{
      return await this.http.POST<Employee, Employee>({
        path: "settings/employee",
        body: employee
      })
    }
    
    /**
     * Aktualisiert einen Mitarbeiter im Backend.
     * @param employee Der zu aktualisierende Mitarbeiter.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit dem aktualisierten Mitarbeiter oder einem Fehler enthält.
     */
    public async updateEmployee(employee: Employee): Promise<HttpFunctionResult<Employee>>{
      return await this.http.PUT<Employee, Employee>({
        path: "settings/employee",
        body: employee
      })
    }

    /**
     * Löscht einen Mitarbeiter anhand seiner ID.
     * @param id Die ID des zu löschenden Mitarbeiters.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit einem boolean success-Feld zurückgibt, das angibt, ob die Anfrage erfolgreich war.
     */
    public async deleteEmployee(id: number): Promise<HttpFunctionResult<any>>{
      return await this.http.DELETE<any>({path: `settings/employee/${id}`})
    }

    /**
     * Aktualisiert eine Mitarbeitergruppe.
     * @param group Die Mitarbeitergruppe, die aktualisiert werden soll.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit den aktualisierten Mitarbeiterdaten enthält.
     */
    public async updateEmployeeGroup(group: EmployeeGroupe): Promise<HttpFunctionResult<Employee>>{
      return await this.http.PUT<EmployeeGroupe, Employee>({
        path: "settings/employeegroup",
        body: group
      })
    }
    
    /**
     * Löscht eine Mitarbeitergruppe anhand der übergebenen ID.
     * @param id Die ID der zu löschenden Mitarbeitergruppe.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit einem boolean success-Feld zurückgibt, das angibt, ob die Anfrage erfolgreich war.
     */
    public async deleteEmployeeGroup(id: number): Promise<HttpFunctionResult<any>>{
      return await this.http.DELETE<any>({path: `settings/employeegroup/${id}`})
    }

    /**
     * Erstellt eine neue Mitarbeitergruppe.
     * @param group Die Mitarbeitergruppe, die erstellt werden soll.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit der erstellten Mitarbeitergruppe enthält.
     */
    public async createEmployeeGroup(group: EmployeeGroupe): Promise<HttpFunctionResult<EmployeeGroupe>>{
      return await this.http.POST<EmployeeGroupe, EmployeeGroupe>({
        path: "settings/employeegroup",
        body: group
      })
    }

    /**
     * Aktualisiert die E-Mail-Einstellungen des Benutzers.
     * @param settings Die neuen E-Mail-Einstellungen.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit den aktualisierten E-Mail-Einstellungen zurückgibt.
     */
    public async updateMail(settings: MailSettings): Promise<HttpFunctionResult<EmployeeGroupe>>{
      return await this.http.PUT<MailSettings, EmployeeGroupe>({
        path: "settings/mail",
        body: settings
      })
    }

    /**
     * Updatet ob Tätigkeiten in Ticket angezeigt werden sollen
     * @param value 
     * @returns 
     */
    public async updateShowTicketTasks(value: boolean): Promise<HttpFunctionResult<boolean>>{
      return await this.http.PUT({
        path: `settings/showTicketTasks?value=${value}`,
      })
    }

    /**
     * Aktualisiert die Einstellungen für die Kundenanmeldung.
     * @param settings Die Einstellungen für die Kundenanmeldung, die aktualisiert werden sollen.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit den aktualisierten Einstellungen für die Kundenanmeldung zurückgibt.
     */
    public async updateCustomerLogin(settings: CustomerLogin): Promise<HttpFunctionResult<CustomerLogin>>{
      return await this.http.PUT<CustomerLogin, CustomerLogin>({
        path: "settings/customerlogin",
        body: settings
      })
    }

    /**
     * Aktualisiert den Ticketbericht mit dem übergebenen HTML.
     * @param html Das HTML, das als Ticketbericht gespeichert werden soll.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit einem boolean success-Feld zurückgibt, das angibt, ob die Aktualisierung erfolgreich war, oder ein HttpFunctionResult-Objekt mit einem Fehler-Objekt, wenn ein Fehler aufgetreten ist.
     */
    public async updateTicketReport(html: string): Promise<HttpFunctionResult<any>>{
      return await this.http.PUT<{data: string}, any>({
        path: "settings/ticketreport",
        body: {data: html}
      })
    }

    /**
     * Erstellt eine neue Aufgaben-Vorlage.
     * @param template Die Aufgaben-Vorlage, die erstellt werden soll.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit der erstellten Aufgaben-Vorlage enthält.
     */
    public async createTaskTemplate(template: TaskTemplateBase): Promise<HttpFunctionResult<TaskTemplateBase>>{
      return await this.http.POST<TaskTemplateBase, TaskTemplateBase>({
        path: "settings/tasktemplate",
        body: template
      })
    }
    
    /**
     * Aktualisiert eine Task-Vorlage.
     * @param template Die zu aktualisierende Task-Vorlage.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit der aktualisierten Task-Vorlage enthält.
     */
    public async updateTaskTemplate(template: TaskTemplateBase): Promise<HttpFunctionResult<TaskTemplateBase>>{
      return await this.http.PUT<TaskTemplateBase, TaskTemplateBase>({
        path: "settings/tasktemplate",
        body: template
      })
    }

    /**
     * Erstellt oder aktualisiert eine Liste von Aufgaben für eine Vorlage mit der angegebenen ID.
     * @param tasks Die Liste der Aufgaben, die erstellt oder aktualisiert werden sollen.
     * @param templateId Die ID der Vorlage, für die die Aufgaben erstellt oder aktualisiert werden sollen.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit einem boolean success-Feld zurückgibt, das angibt, ob die Operation erfolgreich war, oder ein HttpErrorResponse-Objekt im Fehlerfall.
     */
    public async createOrUpdateTaskTemplateItems(tasks: TaskTemplateItem[], templateId: number): Promise<HttpFunctionResult<any>>{
      return await this.http.PUT<TaskTemplateItem[], any>({
        path: `settings/tasktemplate/tasks/${templateId}`,
        body: tasks
      })
    }

    /**
     * Löscht eine Aufgaben-Vorlage anhand der übergebenen ID.
     * @param templateId Die ID der zu löschenden Vorlage.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit einem boolean success-Feld zurückgibt, das angibt, ob die Operation erfolgreich war.
     */
    public async deleteTaskTemplate(templateId: number): Promise<HttpFunctionResult<any>>{
      return await this.http.DELETE<any>({path: `settings/tasktemplate/${templateId}`})
    }

    /**
     * Erstellt ein neues Ticket-Tag.
     * @param tag Das Ticket-Tag, das erstellt werden soll.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit dem erstellten Ticket-Tag enthält.
     */
    public async createTicketTag(tag: TicketTag): Promise<HttpFunctionResult<TicketTag>>{
      return await this.http.POST<TicketTag, TicketTag>({
        path: "settings/tag",
        body: tag
      })
    }

    /**
     * Löscht ein Ticket-Tag anhand der übergebenen ID.
     * @param id - Die ID des zu löschenden Ticket-Tags.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit einem boolean success-Feld zurückgibt, das angibt, ob das Löschen erfolgreich war.
     */
    public async deleteTicketTag(id: number): Promise<HttpFunctionResult<any>>{
      return await this.http.DELETE<any>({path: `settings/tag/${id}`})
    }

    /**
     * Aktualisiert ein Ticket-Tag.
     * @param tag Das Ticket-Tag, das aktualisiert werden soll.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit den aktualisierten Daten zurückgibt.
     */
    public async updateTicketTag(tag: TicketTag): Promise<HttpFunctionResult<any>>{
      return await this.http.PUT<TicketTag, any>({
        path: "settings/tag",
        body: tag
      })
    }

  }

  @Injectable({
    providedIn: 'root'
  })
  export class CRM{

    constructor(
      private http: HttpService
    ){}

    /**
     * Erstellt eine neue Adresse.
     * @param address Die Adresse, die erstellt werden soll.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit der erstellten Adresse enthält.
     */
    public async createAddress(address: Address): Promise<HttpFunctionResult<Address>>{
      return await this.http.POST<Address, Address>({
        path: "addresses",
        body: address
      })
    }
    
    /**
     * Aktualisiert eine Adresse.
     * @param address Die zu aktualisierende Adresse.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit der aktualisierten Adresse enthält.
     */
    public async updateAddress(address: Address): Promise<HttpFunctionResult<Address>>{
      return await this.http.PUT<Address, Address>({
        path: "addresses",
        body: address
      })
    }

    /**
     * Löscht eine Adresse anhand ihrer ID.
     * @param id Die ID der zu löschenden Adresse.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit einem booleschen Erfolgsindikator enthält.
     */
    public async deleteAddress(id: number): Promise<HttpFunctionResult<Address>>{
      return await this.http.DELETE<Address>({path: `addresses/${id}`})
    }

    /**
     * Speichert eine Kontaktperson und gibt ein Promise zurück, das ein HttpFunctionResult-Objekt enthält.
     * @param contactPerson Die Kontaktperson, die gespeichert werden soll.
     * @param addressId Die ID der Adresse, zu der die Kontaktperson gehört.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt enthält, das die gespeicherte Kontaktperson enthält.
     */
    public async saveContactPerson(contactPerson: ContactPerson, addressId: number): Promise<HttpFunctionResult<ContactPerson>>{
      return await this.http.PUT<ContactPerson, ContactPerson>({
        path: `addresses/contactpersons?url=${this.http.getbaseUrl.replace("/api/", "")}&addressId=${addressId}`,
        body: contactPerson
      })
    }

    /**
     * Fügt eine neue Kontaktperson zu einer Adresse hinzu.
     * @param contactPerson Die Kontaktperson, die hinzugefügt werden soll.
     * @param addressId Die ID der Adresse, zu der die Kontaktperson hinzugefügt werden soll.
     * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit der hinzugefügten Kontaktperson enthält.
     */
    public async addContactPerson(contactPerson: ContactPerson, addressId: number): Promise<HttpFunctionResult<ContactPerson>>{
      return await this.http.POST<ContactPerson, ContactPerson>({
        path: `addresses/contactpersons/${addressId}?url=${this.http.getbaseUrl.replace("/api/", "")}`,
        body: contactPerson
      })
    }


  }

}
